import {
  ApplicationConfig,
  isDevMode,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core'
import {
  provideRouter,
  withInMemoryScrolling,
  type InMemoryScrollingFeature,
  type InMemoryScrollingOptions,
} from '@angular/router'
  import { provideStore } from '@ngrx/store'
import { provideStoreDevtools } from '@ngrx/store-devtools'
import { routes } from './app.routes'
import { provideEffects } from '@ngrx/effects'
import { rootReducer } from './store'
import { DatePipe, DecimalPipe } from '@angular/common'
import { AuthenticationEffects } from './store/authentication/authentication.effects'
import {
  provideHttpClient,
  withFetch,
  withInterceptorsFromDi,
} from '@angular/common/http'
import { FakeBackendProvider } from './core/helpers/fake-backend'
import { AuthInterceptorProvider } from './core/interceptors/auth.interceptor'
import { CalendarEffects } from './store/calendar/calendar.effects'
import { KanbanEffects } from './store/kanban/kanban.effects'
import { setBaseUrl, setLarksBaseUrl } from './common/constants'

// Loads /config.json (shipped alongside index.html, outside the compiled bundle)
// so external URLs (the Temus API origin, the Larks API origin) can be changed
// per deployment by editing a file on the server, without rebuilding. Falls back
// to the constants.ts defaults if the file is missing, unreachable, malformed,
// or missing a given key — deploys never hard-fail on this. Uses fetch directly
// rather than HttpClient so it isn't affected by AuthInterceptorProvider or the
// baseUrl it's here to set.
async function loadRuntimeConfig(): Promise<void> {
  try {
    const response = await fetch('config.json', { cache: 'no-store' })
    if (!response.ok) return
    const config = await response.json()
    if (typeof config?.apiUrl === 'string' && config.apiUrl.trim()) {
      setBaseUrl(config.apiUrl.replace(/\/$/, ''))
    }
    if (typeof config?.larksApiUrl === 'string' && config.larksApiUrl.trim()) {
      setLarksBaseUrl(config.larksApiUrl.replace(/\/$/, ''))
    }
  } catch {
    // config.json missing/unreachable — keep the constants.ts defaults.
  }
}

// scroll
const scrollConfig: InMemoryScrollingOptions = {
  scrollPositionRestoration: 'top',
  anchorScrolling: 'enabled',
}

const inMemoryScrollingFeatures: InMemoryScrollingFeature =
  withInMemoryScrolling(scrollConfig)

export const appConfig: ApplicationConfig = {
  providers: [
    provideAppInitializer(loadRuntimeConfig),
    AuthInterceptorProvider,
    FakeBackendProvider,
    DatePipe,
    DecimalPipe,
    provideZoneChangeDetection({
      eventCoalescing: false,
      runCoalescing: false
    }),
    provideRouter(routes, inMemoryScrollingFeatures),
    provideStore(rootReducer),
    provideStoreDevtools({ maxAge: 25, logOnly: !isDevMode() }),
    provideEffects(CalendarEffects),
    provideEffects(AuthenticationEffects,KanbanEffects),
    provideHttpClient(withFetch(), withInterceptorsFromDi()),
  ],
}
